// src/components/ContactPage.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import emailjs from 'emailjs-com';

// EmailJS credentials. These are public by design (they ship in the client
// bundle and are protected via the allowed-domains + rate limits configured in
// the EmailJS dashboard for drlbusiness.sro@gmail.com).
const EMAILJS_SERVICE_ID = 'service_hwdzruf';
const EMAILJS_TEMPLATE_ID = 'template_qy62e44';
const EMAILJS_PUBLIC_KEY = 'Y_uodjZiwx6GVNXaE';
emailjs.init(EMAILJS_PUBLIC_KEY);

// Styled Components
const ContactContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    padding: 80px 0 50px;

    @media (max-width: 768px) {
        padding: 60px 0 30px;
    }
`;

const ContactContent = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const PageHeader = styled(motion.div)`
    text-align: center;
    margin-bottom: 3rem;

    h1 {
        font-size: 3rem;
        font-weight: 800;
        color: #1f2937;
        margin-bottom: 1rem;

        @media (max-width: 768px) {
            font-size: 2rem;
            margin-bottom: 0.5rem;
        }
    }

    p {
        font-size: 1.25rem;
        color: #6b7280;
        max-width: 600px;
        margin: 0 auto;
        line-height: 1.6;

        @media (max-width: 768px) {
            font-size: 1rem;
            padding: 0 1rem;
        }
    }
`;

const ContactGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    margin-bottom: 4rem;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 2rem;
        margin-bottom: 3rem;
    }
`;

const ContactFormSection = styled(motion.div)`
    background: white;
    padding: 2.5rem;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

    h2 {
        font-size: 1.75rem;
        font-weight: 700;
        color: #1f2937;
        margin-bottom: 1.5rem;
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: -0.5rem;
            left: 0;
            width: 60px;
            height: 3px;
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            border-radius: 2px;
        }
    }

    @media (max-width: 768px) {
        padding: 1.5rem;
        border-radius: 16px;

        h2 {
            font-size: 1.5rem;
        }
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 1.25rem;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    font-weight: 600;
    color: #374151;
    font-size: 0.9rem;
`;

const Input = styled.input`
    padding: 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s ease;
    background: white;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:invalid {
        border-color: #e5e7eb;
    }

    @media (max-width: 768px) {
        padding: 0.875rem;
    }
`;

const TextArea = styled.textarea`
    padding: 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 1rem;
    min-height: 120px;
    resize: vertical;
    transition: all 0.2s ease;
    font-family: inherit;
    background: white;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    @media (max-width: 768px) {
        padding: 0.875rem;
        min-height: 100px;
    }
`;

const SubmitButton = styled(motion.button)`
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    color: white;
    border: none;
    padding: 1.125rem 2rem;
    border-radius: 8px;
    font-size: 1.125rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 0.5rem;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }

    @media (max-width: 768px) {
        padding: 1rem 1.5rem;
        font-size: 1rem;
    }
`;

const ContactInfoSection = styled(motion.div)`
    h2 {
        font-size: 1.75rem;
        font-weight: 700;
        color: #1f2937;
        margin-bottom: 1.5rem;
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: -0.5rem;
            left: 0;
            width: 60px;
            height: 3px;
            background: linear-gradient(135deg, #10b981, #059669);
            border-radius: 2px;
        }
    }

    @media (max-width: 768px) {
        h2 {
            font-size: 1.5rem;
        }
    }
`;

const ContactCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        gap: 1rem;
        margin-bottom: 1.5rem;
    }
`;

const ContactCard = styled(motion.div)`
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    border-left: 4px solid #3b82f6;

    h3 {
        font-size: 1.125rem;
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    p {
        color: #6b7280;
        line-height: 1.6;
        margin: 0;
        font-size: 0.95rem;
    }

    a {
        color: #3b82f6;
        text-decoration: none;
        font-weight: 500;

        &:hover {
            text-decoration: underline;
        }
    }

    @media (max-width: 768px) {
        padding: 1.25rem;

        h3 {
            font-size: 1rem;
        }

        p {
            font-size: 0.9rem;
        }
    }
`;

const BusinessInfo = styled(motion.div)`
    background: linear-gradient(135deg, #1f2937, #374151);
    color: white;
    padding: 2rem;
    border-radius: 15px;

    h3 {
        font-size: 1.25rem;
        font-weight: 700;
        margin-bottom: 1.25rem;
        color: #fbbf24;
    }

    @media (max-width: 768px) {
        padding: 1.5rem;

        h3 {
            font-size: 1.125rem;
            margin-bottom: 1rem;
        }
    }
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const InfoItem = styled.div`
    p {
        margin: 0.25rem 0;
        color: #d1d5db;
        font-size: 0.9rem;
        line-height: 1.5;

        strong {
            color: white;
            font-weight: 600;
        }
    }

    @media (max-width: 768px) {
        p {
            font-size: 0.85rem;
        }
    }
`;

const MapSection = styled(motion.section)`
    margin-top: 3rem;

    h2 {
        font-size: 1.75rem;
        font-weight: 700;
        color: #1f2937;
        text-align: center;
        margin-bottom: 1.5rem;
    }

    @media (max-width: 768px) {
        margin-top: 2rem;

        h2 {
            font-size: 1.5rem;
            margin-bottom: 1rem;
        }
    }
`;

const MapContainer = styled.div`
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    height: 400px;

    iframe {
        width: 100%;
        height: 100%;
        border: none;
    }

    @media (max-width: 768px) {
        height: 300px;
        border-radius: 12px;
    }
`;

const SuccessMessage = styled(motion.div)`
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    margin-top: 1rem;

    h3 {
        font-size: 1.5rem;
        margin-bottom: 1rem;
    }

    p {
        margin: 0;
        opacity: 0.9;
        line-height: 1.5;
    }

    @media (max-width: 768px) {
        padding: 1.5rem;

        h3 {
            font-size: 1.25rem;
            margin-bottom: 0.75rem;
        }

        p {
            font-size: 0.95rem;
        }
    }
`;

const ErrorMessage = styled(motion.div)`
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    padding: 1rem;
    border-radius: 8px;
    text-align: center;
    margin-bottom: 1rem;

    p {
        margin: 0;
        font-size: 0.9rem;
    }
`;

// Main ContactPage Component
const ContactPage: React.FC = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            // EmailJS template parameters (must match the variables used in the
            // EmailJS template "template_qy62e44").
            const templateParams = {
                from_name: `${formData.firstName} ${formData.lastName}`.trim(),
                from_email: formData.email,
                phone: formData.phone,
                subject: formData.subject,
                message: formData.message,
                to_email: 'drlbusiness.sro@gmail.com'
            };

            await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                templateParams,
                EMAILJS_PUBLIC_KEY
            );

            setIsSubmitted(true);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
        } catch (error) {
            console.error('Error sending email:', error);
            setError('Failed to send message. Please try again, or email drlbusiness.sro@gmail.com directly.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ContactContainer>
            <ContactContent>
                <PageHeader
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1>Contact Us</h1>
                    <p>Get in touch with DRL Business for bookings, inquiries, or custom tour arrangements. We're here to help you plan your perfect Central European adventure.</p>
                </PageHeader>

                <ContactGrid>
                    {/* Contact Form */}
                    <ContactFormSection
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <h2>Send us a Message</h2>

                        {isSubmitted ? (
                            <SuccessMessage
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                <h3>Thank You!</h3>
                                <p>Your message has been sent successfully. We'll get back to you within 24 hours.</p>
                            </SuccessMessage>
                        ) : (
                            <Form onSubmit={handleSubmit}>
                                {error && (
                                    <ErrorMessage
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <p>{error}</p>
                                    </ErrorMessage>
                                )}

                                <FormRow>
                                    <FormGroup>
                                        <Label htmlFor="firstName">First Name *</Label>
                                        <Input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label htmlFor="lastName">Last Name *</Label>
                                        <Input
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label htmlFor="email">Email Address *</Label>
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label htmlFor="phone">Phone Number</Label>
                                        <Input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                        />
                                    </FormGroup>
                                </FormRow>

                                <FormGroup>
                                    <Label htmlFor="subject">Subject *</Label>
                                    <Input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="message">Message *</Label>
                                    <TextArea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Tell us about your travel plans, group size, dates and any special requirements..."
                                    />
                                </FormGroup>

                                <SubmitButton
                                    type="submit"
                                    disabled={isSubmitting}
                                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </SubmitButton>
                            </Form>
                        )}
                    </ContactFormSection>

                    {/* Contact Information */}
                    <ContactInfoSection
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                    >
                        <h2>Get in Touch</h2>

                        <ContactCards>
                            <ContactCard
                                whileHover={{ y: -5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <h3>📞 Phone & WhatsApp</h3>
                                <p>
                                    <a href="tel:+421951074538">+421 951 074 538</a><br />
                                    Available for calls and WhatsApp messages
                                </p>
                            </ContactCard>

                            <ContactCard
                                whileHover={{ y: -5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <h3>✉️ Email</h3>
                                <p>
                                    <a href="mailto:drlbusiness.sro@gmail.com">drlbusiness.sro@gmail.com</a><br />
                                    We typically respond within 24 hours
                                </p>
                            </ContactCard>

                            <ContactCard
                                whileHover={{ y: -5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <h3>🏢 Address</h3>
                                <p>
                                    Budatinska 3230/16A<br />
                                    85106 Bratislava, Slovakia, EU
                                </p>
                            </ContactCard>
                        </ContactCards>

                        <BusinessInfo
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                        >
                            <h3>Business Information</h3>
                            <InfoGrid>
                                <InfoItem>
                                    <p><strong>Company:</strong> DRL business s.r.o.</p>
                                    <p><strong>Business ID:</strong> 56927436</p>
                                    <p><strong>VAT ID:</strong> SK2122508443</p>
                                </InfoItem>
                                <InfoItem>
                                    <p><strong>Registered:</strong> Commercial Register Bratislava 3</p>
                                    <p><strong>Section:</strong> B, Insert 187453</p>
                                    <p><strong>E-mailbox:</strong> E0008160479</p>
                                </InfoItem>
                            </InfoGrid>
                        </BusinessInfo>
                    </ContactInfoSection>
                </ContactGrid>

                {/* Map Section */}
                <MapSection
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    <h2>Find Us</h2>
                    <MapContainer>
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2660.847284908907!2d17.15031227680056!3d48.17087134675932!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476c8f3f8c2b7b0f%3A0x7a0b1a0b0b0b0b0b!2sBudatinska%203230%2F16A%2C%20851%2006%20Bratislava%2C%20Slovakia!5e0!3m2!1sen!2sus!4v1690000000000!5m2!1sen!2sus"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="DRL Business Location"
                        ></iframe>
                    </MapContainer>
                </MapSection>
            </ContactContent>
        </ContactContainer>
    );
};

export default ContactPage;